import { useState, useReducer, useCallback, type JSX } from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Button,
    emitNotification,
    ButtonGroup,
    ButtonSpec,
    Divider,
    InformationModal,
} from "@impero-com/ui";
import uuidv4 from "uuid/v4";

import {
    UserTagCategoryView,
    UserTagView,
    UpdatableTag,
} from "api/generated/model";
import { Drawer } from "components/drawer";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { StringInput } from "components/form/legacyFields/stringInput";
import { TagManagementTable } from "components/tag/tagManagementTable";
import IconDelete from "img/delete.svg";

import { useCreateTagMutation } from "./queries/useCreateTagMutation";
import { useDeleteTagCategoryMutation } from "./queries/useDeleteTagCategoryMutation";
import { useDeleteTagMutation } from "./queries/useDeleteTagMutation";
import {
    CannotRenameToInactiveTagError,
    useUpdateTagMutation,
} from "./queries/useUpdateTagMutation";

interface EditTagCategoryDrawerProps {
    // Tag Category with its name and id.
    category: UserTagCategoryView;

    // Existing tags in the given category.
    tags: Array<UserTagView>;

    // A callback which is invoked when the drawer is closed.
    // If isChanged = true, the parent component is instructed to reload data.
    onClose: (isChanged: boolean) => void;

    // A callback which is invoked when user click "Delete category" and confirms it.
    onTagCategoryDeleted: (categoryId: string) => void;
}

const EditTagCategoryDrawer = (
    props: EditTagCategoryDrawerProps
): JSX.Element => {
    const { category, tags, onClose, onTagCategoryDeleted } = props;
    const [showDeleteCategoryConfirmation, setShowDeleteCategoryConfirmation] =
        useState(false);
    const [isChanged, setIsChanged] = useState(false);

    const buttons: ButtonSpec[] = [
        {
            id: "delete",
            label: t("TagsList_EditCategoryDrawer_Action_DeleteCategory"),
            type: "link",
            onClick: () => setShowDeleteCategoryConfirmation(true),
            icon: { svg: IconDelete },
            disabled: false,
        },
    ];

    return (
        <>
            <Drawer
                buildHeader={() => t("TagsList_EditCategoryDrawer_Header")}
                buildFooter={() => <ButtonGroup buttonSpecs={buttons} />}
                onCancel={() => onClose(isChanged)}
            >
                <StringInput
                    type="READ_ONLY"
                    label={t("TagsList_EditCategoryDrawer_Field_CategoryName")}
                    value={category.name}
                />
                <Divider />
                <TagTable
                    initialTags={tags}
                    tagCategoryId={category.id}
                    onChange={() => setIsChanged(true)}
                />
            </Drawer>

            <DeleteCategoryConfirmationDialog
                category={category}
                show={showDeleteCategoryConfirmation}
                onDeleted={() => onTagCategoryDeleted(category.id)}
                onCancel={() => setShowDeleteCategoryConfirmation(false)}
            />
        </>
    );
};

interface DeleteCategoryConfirmationDialogProps {
    // The category which is about to be deleted
    category: UserTagCategoryView;

    // Should the dialogue be shown (usually by default No)
    show: boolean;

    // A callback which is invoked when the category is deleted
    // NOTE: the component invokes API and this callback is called only when the category is already deleted.
    onDeleted: () => void;

    // A callback which is invoked when the deletion is canceled.
    onCancel: () => void;
}

const DeleteCategoryConfirmationDialog = (
    props: DeleteCategoryConfirmationDialogProps
): JSX.Element => {
    const { category, show, onDeleted, onCancel } = props;

    const { mutate: deleteTagCategory, isPending } =
        useDeleteTagCategoryMutation(onDeleted, category.name);

    return (
        <InformationModal
            open={show}
            onClose={onCancel}
            type="error"
            icon="delete"
            title={t(
                "TagsList_EditCategoryDrawer_DeteleCategoryConfirmation_Title"
            )}
            footer={
                <>
                    <Button onClick={onCancel}>{t("Cancel")}</Button>
                    <Button
                        variant="destructive"
                        disabled={isPending}
                        loading={isPending}
                        onClick={() =>
                            deleteTagCategory({ tagCategoryId: category.id })
                        }
                    >
                        {t("Delete")}
                    </Button>
                </>
            }
        >
            {t("TagsList_EditCategoryDrawer_DeteleCategoryConfirmation_Text", {
                tagCategoryName: category.name,
            })}
        </InformationModal>
    );
};

interface TagTableProps {
    tagCategoryId: string;
    initialTags: Array<UserTagView>;
    onChange: () => void;
}

// This component implements Optimistic UI pattern, meaning: it assumes that all operations with API are successful
// and the change is displayed instantly, so it does not interrupts user's flow.
// In case if operation fails, the change is reverted and use is notified about error.
const TagTable = (props: TagTableProps): JSX.Element => {
    const { onChange, tagCategoryId, initialTags } = props;
    const initialState: TagTableState = {
        tags: initialTags,
        disabledTagIds: [],
    };

    const { mutateAsync: createTag } = useCreateTagMutation();
    const { mutateAsync: updateTag } = useUpdateTagMutation();
    const { mutateAsync: deleteTag } = useDeleteTagMutation();

    const [state, dispatch] = useReducer(tagTableReducer, initialState);
    const { tags, disabledTagIds } = state;

    const onAdd = useCallback(
        (name: string) => {
            const temporalTagId = "TEMPORAL-ID-" + uuidv4();
            const newTemporalTag: UserTagView = {
                id: temporalTagId,
                tagCategoryId,
                name,
                active: true,
            };
            dispatch({ type: "ADD", newTemporalTag });
            createTag({ tag: { tagCategoryId, name } })
                .then((createdTag) => {
                    dispatch({
                        type: "ADD_FINALIZE",
                        temporalTagId,
                        createdTag,
                    });
                    onChange();
                })
                .catch(() => {
                    dispatch({ type: "ADD_ROLLBACK", temporalTagId });
                    emitNotification(
                        "error",
                        t(
                            "TagsList_EditCategoryDrawer_Notification_CreateTagFailed"
                        )
                    );
                });
        },
        [tagCategoryId, onChange, createTag]
    );

    const onDelete = useCallback(
        (tagId: string) => {
            const index = tags.findIndex((t) => t.id === tagId);
            if (index < 0) {
                console.error("onDelete: could not find tag by id = ", tagId);
                return;
            }
            const tag = tags[index];

            dispatch({ type: "DELETE", tagId });
            deleteTag({ tagId })
                .then(() => {
                    onChange();
                })
                .catch(() => {
                    dispatch({ type: "DELETE_ROLLBACK", index, tag });
                    emitNotification(
                        "error",
                        t(
                            "TagsList_EditCategoryDrawer_Notification_DeleteTagFailed"
                        )
                    );
                });
        },
        [onChange, tags, deleteTag]
    );

    const onRename = useCallback(
        (tagId: string, newName: string) => {
            const payload: UpdatableTag = { name: newName };
            const oldTagVersion = tags.find((t) => t.id === tagId);
            if (!oldTagVersion) {
                console.error("onRename: could not find tag by id = ", tagId);
                return;
            }
            const newTagVersion: UserTagView = {
                ...oldTagVersion,
                name: newName,
            };
            dispatch({ type: "UPDATE", tag: newTagVersion });
            updateTag({ tagId, updatableTag: payload })
                .then((updatedTag) => {
                    dispatch({ type: "UPDATE_FINALIZE", tag: updatedTag });
                    onChange();
                })
                .catch((err) => {
                    dispatch({ type: "UPDATE_ROLLBACK", tag: oldTagVersion });
                    if (err instanceof CannotRenameToInactiveTagError) {
                        // This message is long, so we allocate a bit more time for the user to read it.
                        emitNotification(
                            "error",
                            t(
                                "TagsList_EditCategoryDrawer_Notification_CannotRenameToInactiveTag"
                            ),
                            { duration: "DOUBLE" }
                        );
                    } else {
                        emitNotification(
                            "error",
                            t(
                                "TagsList_EditCategoryDrawer_Notification_RenameTagFailed"
                            )
                        );
                    }
                });
        },
        [onChange, tags, updateTag]
    );

    const getTagId = useCallback((tag: UserTagView) => tag.id, []);
    const getTagName = useCallback((tag: UserTagView) => tag.name, []);
    return (
        <BaseFormDisplay label={t("TagsList_EditCategoryDrawer_Field_Tags")}>
            <TagManagementTable
                tags={tags}
                getTagId={getTagId}
                getTagName={getTagName}
                askConfirmationOnDelete={true}
                onRename={onRename}
                onDelete={onDelete}
                onAdd={onAdd}
                disabledIds={disabledTagIds}
            />
        </BaseFormDisplay>
    );
};

interface TagTableState {
    tags: Array<UserTagView>;

    // Ids of tags which is not yet finalized.
    // Consider when a new tag added. It's immediately is show in TagManagementTable, but
    // we don't want user to be able to rename or delete it, because we did not yet get response
    // from the server.
    disabledTagIds: Array<string>;
}

type TagTableAction =
    | { type: "ADD"; newTemporalTag: UserTagView }
    | { type: "ADD_FINALIZE"; temporalTagId: string; createdTag: UserTagView }
    | { type: "ADD_ROLLBACK"; temporalTagId: string }
    | { type: "DELETE"; tagId: string }
    | { type: "DELETE_ROLLBACK"; index: number; tag: UserTagView }
    | { type: "UPDATE"; tag: UserTagView }
    | { type: "UPDATE_FINALIZE"; tag: UserTagView }
    | { type: "UPDATE_ROLLBACK"; tag: UserTagView };

const tagTableReducer = (
    state: TagTableState,
    action: TagTableAction
): TagTableState => {
    switch (action.type) {
        case "ADD": {
            // Add a temporal tag.
            // Add it to the disabled list, so it cannot be edited, until we get a crated tag from the API (see ADD_FINALIZE)
            const { newTemporalTag } = action;
            return {
                ...state,
                tags: [newTemporalTag, ...state.tags],
                disabledTagIds: [...state.disabledTagIds, newTemporalTag.id],
            };
        }
        case "ADD_ROLLBACK": {
            // Undo ADD
            const { temporalTagId } = action;
            return {
                ...state,
                tags: state.tags.filter((t) => t.id !== temporalTagId),
                disabledTagIds: state.disabledTagIds.filter(
                    (id) => id !== temporalTagId
                ),
            };
        }
        case "ADD_FINALIZE": {
            // Replace the temporalTag from ADD with the one received from API.
            // Remove the temporalTagId from the disabled list, so it can be edited.
            const { temporalTagId, createdTag } = action;
            return {
                ...state,
                tags: replaceTag(state.tags, temporalTagId, createdTag),
                disabledTagIds: state.disabledTagIds.filter(
                    (id) => id !== temporalTagId
                ),
            };
        }
        case "DELETE": {
            const { tagId } = action;
            return {
                ...state,
                tags: state.tags.filter((t) => t.id !== tagId),
            };
        }
        case "DELETE_ROLLBACK": {
            const { index, tag } = action;
            const { tags } = state;
            return {
                ...state,
                // Immutably insert `tag` at `index` position
                tags: [...tags.slice(0, index), tag, ...tags.slice(index)],
            };
        }
        case "UPDATE":
        case "UPDATE_FINALIZE":
        case "UPDATE_ROLLBACK": {
            const { tag } = action;
            return {
                ...state,
                tags: replaceTag(state.tags, tag.id, tag),
            };
        }
        default: {
            return assertNever(action);
        }
    }
};

const replaceTag = (
    tags: Array<UserTagView>,
    tagId: string,
    newTag: UserTagView
): Array<UserTagView> => {
    return tags.map((t) => {
        if (t.id === tagId) {
            return newTag;
        } else {
            return t;
        }
    });
};

export { EditTagCategoryDrawer };
